import React, { useState, useEffect, useRef } from 'react'
import { colors } from './fieldTheme'
import { authorOf, isMine, editComment } from './fieldSummary'
import { formatDateTime, nowSeconds } from './fieldDates'
import { DotsIcon, PencilIcon, TrashIcon, ClearIcon, CheckIcon } from './FieldIcons'
import { BottomSheet, SheetGroup, SheetRow, PrimaryButton, SecondaryButton } from './FieldUI'

// Liste de commentaires dans les volets (canvas W19c et W20, avec W11, W12 et W12b). Décisions 28 et 31 :
// des lignes simples séparées par un filet, sans cartes (retour d'Alexandra du 24/10/2026) ;
// seul l'auteur modifie ou supprime son commentaire, via « ⋯ » ; la modification se fait sur place,
// l'ancien texte part dans l'historique ; un bandeau propose « Annuler » juste après.

const TOAST_DURATION = 6000

// Bandeau de confirmation collé en haut de l'écran (canvas W12b)
export function UndoToast({ toast, onUndo, onDismiss, inSheet })
{
	useEffect(() => {
		if (!toast)
			return ;
		const timer = setTimeout(onDismiss, TOAST_DURATION)
		return () => clearTimeout(timer)
	}, [toast, onDismiss])
	if (!toast)
		return null
	return (
		<div style={{ position: 'sticky', top: 0, zIndex: 3, padding: inSheet ? '0 0 8px' : '8px 20px 0', background: inSheet ? colors.surface : colors.background }}>
			<div role="status" style={{ borderRadius: 14, background: '#DCFCE7', color: '#14532D', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '0 4px 0 20px', minHeight: 52 }}>
				<span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 17, fontWeight: 700 }}>
					<CheckIcon size={22} />
					{toast.text}
				</span>
				{toast.undo && (
					<button
						type="button"
						onClick={onUndo}
						style={{ backgroundImage: 'none', backgroundColor: 'transparent', minHeight: 48, padding: '0 12px', border: 0, color: '#14532D', fontSize: 17, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, fontFamily: 'inherit', cursor: 'pointer' }}
					>
						Annuler
					</button>
				)}
			</div>
		</div>
	)
}

function Avatar({ initials })
{
	return (
		<span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 999, background: colors.primary, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flex: 'none' }}>{initials}</span>
	)
}

function ThemeTag({ name })
{
	return (
		<span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', minHeight: 30, padding: '0 10px', borderRadius: 999, background: '#E8EBF5', color: colors.text, fontSize: 15, fontWeight: 600 }}>{name}</span>
	)
}

function EditForm({ item, author, now, onCancel, onSave })
{
	const [draft, setDraft] = useState(item.comment.comment)
	const fieldRef = useRef(null)
	const empty = draft.trim() === ''
	const fieldId = 'field-edit-comment-' + item.comment.id

	useEffect(() => {
		const field = fieldRef.current
		if (!field)
			return ;
		field.focus()
		field.setSelectionRange(field.value.length, field.value.length)
	}, [])

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 0' }}>
			<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
				<Avatar initials={author.initials} />
				<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
					<span style={{ fontSize: 17, fontWeight: 700 }}>{author.name}</span>
					<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{formatDateTime(item.comment.commentTime, now)}</span>
				</span>
			</div>
			{item.themeName && <ThemeTag name={item.themeName} />}
			<label htmlFor={fieldId} style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>Modifier mon commentaire</label>
			<textarea
				id={fieldId}
				ref={fieldRef}
				rows={3}
				className="browser-default"
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				style={{ width: '100%', minHeight: 104, margin: 0, padding: '12px 14px', borderRadius: 14, border: '2px solid ' + colors.primary, background: colors.surface, fontSize: 17, lineHeight: 1.45, fontFamily: 'inherit', color: colors.text, resize: 'vertical', boxSizing: 'border-box' }}
			/>
			{empty && <p style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>Le commentaire est vide. Pour le retirer, annulez puis choisissez Supprimer.</p>}
			<div style={{ display: 'flex', gap: 16 }}>
				<SecondaryButton onClick={onCancel}>Annuler</SecondaryButton>
				<PrimaryButton icon={<CheckIcon size={22} />} disabled={empty} onClick={() => onSave(draft.trim())}>Enregistrer</PrimaryButton>
			</div>
		</div>
	)
}

function CommentLine({ item, author, mine, now, onActions })
{
	const comment = item.comment
	return (
		<div style={{ display: 'flex', gap: 12, padding: '16px 0' }}>
			<Avatar initials={author.initials} />
			<div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
				<span style={{ fontSize: 17, fontWeight: 700 }}>{author.name}</span>
				<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>{formatDateTime(comment.commentTime, now)}</span>
				{comment.commentEditTime && <span style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{'modifié le ' + formatDateTime(comment.commentEditTime, now)}</span>}
				{item.themeName && <ThemeTag name={item.themeName} />}
				<p style={{ fontSize: 17, fontWeight: 500, marginTop: 4, overflowWrap: 'break-word', whiteSpace: 'pre-line' }}>{comment.comment}</p>
			</div>
			{mine && (
				<button
					type="button"
					aria-label="Actions sur mon commentaire"
					onClick={onActions}
					style={{ backgroundImage: 'none', backgroundColor: 'transparent', width: 48, height: 48, flex: 'none', borderRadius: 999, border: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, marginTop: -4, cursor: 'pointer' }}
				>
					<DotsIcon />
				</button>
			)}
		</div>
	)
}

// items : [{ comment, themeName }] ; onChange(commentModifié) ; onToast({ text, undo })
export default function FieldComments({ title, comments, user, now, onChange, onToast })
{
	const [actionsFor, setActionsFor] = useState(null)
	const [editing, setEditing] = useState(null)
	const current = comments.find(item => item.comment.id === actionsFor)

	const replace = (item, next, text) => {
		onChange(next)
		onToast({ text, undo: () => onChange(item.comment) })
	}

	return (
		<React.Fragment>
			{title && <h3 style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: colors.textSecondary, letterSpacing: '.04em', textTransform: 'uppercase', margin: 0, paddingBottom: 4 }}>{title + ' (' + comments.length + ')'}</h3>}
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{comments.map((item, index) => {
					const author = authorOf(item.comment.commentUser, user)
					const separator = { borderTop: '1px solid ' + colors.separator, borderBottom: index === comments.length - 1 ? '1px solid ' + colors.separator : 0 }
					if (editing === item.comment.id)
					{
						return (
							<div key={item.comment.id} style={separator}>
								<EditForm
									item={item}
									author={author}
									now={now}
									onCancel={() => setEditing(null)}
									onSave={(text) => {
										setEditing(null)
										if (text !== item.comment.comment)
											replace(item, editComment(item.comment, text, nowSeconds()), 'Commentaire modifié')
									}}
								/>
							</div>
						)
					}
					return (
						<div key={item.comment.id} style={separator}>
							<CommentLine item={item} author={author} mine={isMine(item.comment.commentUser, user)} now={now} onActions={() => setActionsFor(item.comment.id)} />
						</div>
					)
				})}
			</div>
			{current && (
				<BottomSheet title="Mon commentaire" text={(current.themeName ? current.themeName + ', ' : '') + formatDateTime(current.comment.commentTime, now)} onClose={() => setActionsFor(null)}>
					<SheetGroup>
						<SheetRow label="Modifier" icon={<PencilIcon />} onClick={() => {
							setActionsFor(null)
							setEditing(current.comment.id)
						}} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Supprimer" icon={<TrashIcon />} danger onClick={() => {
							setActionsFor(null)
							replace(current, { ...current.comment, commentDeletedTime: nowSeconds() }, 'Commentaire supprimé')
						}} />
					</SheetGroup>
					<SheetGroup>
						<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setActionsFor(null)} />
					</SheetGroup>
				</BottomSheet>
			)}
		</React.Fragment>
	)
}
